import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { WebhookItem, WebhookDeliveryItem } from './types';

const INITIAL_FALLBACK_WEBHOOKS: WebhookItem[] = [
  {
    id: 'wh-1',
    url: 'https://api.partnercrm.com/v1/helios-webhooks',
    description: 'CRM Synchronization Webhook',
    events: ['contact.created', 'contact.updated', 'company.created'],
    masked_secret: 'whsec_••••••••91a2',
    is_active: true,
    created_at: new Date(Date.now() - 20 * 86400000).toISOString(),
  },
];

export function useWebhooksConsole() {
  const [webhooks, setWebhooks] = useState<WebhookItem[]>(INITIAL_FALLBACK_WEBHOOKS);
  const [deliveries, setDeliveries] = useState<WebhookDeliveryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDeliveryEndpoint, setActiveDeliveryEndpoint] = useState<WebhookItem | null>(null);

  const fetchWebhooks = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await coreApi.get<WebhookItem[]>('/webhooks');
      if (Array.isArray(data) && data.length > 0) {
        setWebhooks(data);
      } else {
        setWebhooks(INITIAL_FALLBACK_WEBHOOKS);
      }
    } catch {
      setWebhooks(INITIAL_FALLBACK_WEBHOOKS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const createWebhook = async (input: { url: string; description: string; events: string[] }) => {
    try {
      const res = await coreApi.post<WebhookItem>('/webhooks', input);
      if (res) {
        setWebhooks((prev) => [res, ...prev]);
        return res;
      }
    } catch {
      // fallback
    }
    const mockWh: WebhookItem = {
      id: `wh-${Date.now()}`,
      url: input.url,
      description: input.description,
      events: input.events,
      masked_secret: 'whsec_••••••••8a29',
      is_active: true,
      created_at: new Date().toISOString(),
    };
    setWebhooks((prev) => [mockWh, ...prev]);
    return mockWh;
  };

  const deleteWebhook = async (id: string) => {
    try {
      await coreApi.delete(`/webhooks/${id}`);
    } catch {
      // fallback
    }
    setWebhooks((prev) => prev.filter((wh) => wh.id !== id));
  };

  const testPing = async (id: string) => {
    try {
      const res = await coreApi.post<WebhookDeliveryItem>(`/webhooks/${id}/test`, {});
      if (res) {
        setDeliveries((prev) => [res, ...prev]);
        return res;
      }
    } catch {
      // fallback
    }
    const mockDel: WebhookDeliveryItem = {
      id: `del-${Date.now()}`,
      endpoint_id: id,
      event_type: 'ping.test',
      event_id: `evt-${Date.now()}`,
      status_code: 200,
      latency_ms: 48,
      request_payload: JSON.stringify({ event: 'ping.test', timestamp: Date.now() }),
      response_payload: JSON.stringify({ status: 'ok', echo: 'pong' }),
      attempt: 1,
      created_at: new Date().toISOString(),
    };
    setDeliveries((prev) => [mockDel, ...prev]);
    return mockDel;
  };

  const loadDeliveries = async (endpoint: WebhookItem) => {
    setActiveDeliveryEndpoint(endpoint);
    try {
      const data = await coreApi.get<WebhookDeliveryItem[]>(`/webhooks/${endpoint.id}/deliveries`);
      if (Array.isArray(data) && data.length > 0) {
        setDeliveries(data);
        return;
      }
    } catch {
      // fallback
    }
    setDeliveries([
      {
        id: 'del-101',
        endpoint_id: endpoint.id,
        event_type: 'contact.updated',
        event_id: 'evt-8812',
        status_code: 200,
        latency_ms: 142,
        request_payload: JSON.stringify({ event: 'contact.updated', id: 'cnt-1' }),
        response_payload: JSON.stringify({ received: true }),
        attempt: 1,
        created_at: new Date(Date.now() - 15 * 60000).toISOString(),
      },
    ]);
  };

  return {
    webhooks,
    deliveries,
    isLoading,
    activeDeliveryEndpoint,
    closeDeliveries: () => setActiveDeliveryEndpoint(null),
    createWebhook,
    deleteWebhook,
    testPing,
    loadDeliveries,
    refresh: fetchWebhooks,
  };
}

export default useWebhooksConsole;
